import { Controller } from "@hotwired/stimulus";

/**
 * Derived visuals for the simplified scoring canvas (F4). Co-mounts with
 * `scoring-config` on the same wrapper and reads the per-band form rows
 * (`[data-range-row]` with a `[data-field='starts_at']` threshold input) as
 * the single source of truth.
 *
 * It renders, on every edit:
 *   1. A read-only colored stripe + numeric axis (preview of the score space).
 *   2. Each row's derived `Range` cell and color swatch.
 *   3. One validation banner — bands are contiguous by construction, so the
 *      only failure mode is non-increasing thresholds.
 *
 * Replaces the former `scoring-number-line` (draggable handles) and
 * `scoring-validation` controllers — gaps/overlaps can no longer occur.
 */

// A 10-stop green→red gradient (the bands table caps at 10 rows). Mirrors
// SurveyHelper::SCORING_BAND_COLORS so the canvas, table swatches, and
// distribution rail agree.
const BAND_PALETTE: Array<{ bg: string; text: string }> = [
  { bg: "#D1FAE5", text: "#065F46" },
  { bg: "#DCFCE7", text: "#166534" },
  { bg: "#ECFCCB", text: "#3F6212" },
  { bg: "#FEF9C3", text: "#854D0E" },
  { bg: "#FEF3C7", text: "#92400E" },
  { bg: "#FFEDD5", text: "#9A3412" },
  { bg: "#FED7AA", text: "#7C2D12" },
  { bg: "#FECACA", text: "#991B1B" },
  { bg: "#FECDD3", text: "#9F1239" },
  { bg: "#FCA5A5", text: "#7F1D1D" },
];

// Pick a band's color by sampling the palette evenly across the band count,
// not by raw index — so 3 bands span green→amber→red instead of three
// near-identical greens. When the survey's score direction is
// "higher is better" the gradient is flipped so the lowest band is red and
// the highest is green — green still means "good outcome" either way.
// Mirrors SurveyHelper#scoring_band_color.
export function bandColor(
  index: number,
  total: number,
  higherIsWorse = true,
): { bg: string; text: string } {
  if (total <= 1) return BAND_PALETTE[0];
  const effectiveIndex = higherIsWorse ? index : total - 1 - index;
  const position = effectiveIndex / (total - 1);
  return BAND_PALETTE[Math.round(position * (BAND_PALETTE.length - 1))];
}

interface Band {
  index: number;
  startsAt: number | null;
  min: number;
  max: number;
  label: string;
  bg: string;
  text: string;
  row: HTMLElement;
}

export default class extends Controller {
  static targets = [
    "stripe",
    "axis",
    "bandCount",
    "okBanner",
    "okMessage",
    "errorBanner",
    "errorMessage",
    "bandsSummary",
  ];

  static values = {
    max: { type: Number, default: 0 },
    higherIsWorse: { type: Boolean, default: true },
    bandCounts: { type: Object, default: {} as Record<string, number> },
    okTemplate: { type: String, default: "%{count} bands cover 0–%{max}." },
    notIncreasingTemplate: {
      type: String,
      default: "Band %{index} starts at %{value}, which is not above the band before it.",
    },
    summaryTemplate: { type: String, default: "%{count} bands · scale 0–%{max} · %{responses} responses analyzed" },
    summaryTemplateZero: { type: String, default: "No bands yet" },
    bandCountTemplate: { type: String, default: "%{count} bands" },
  };

  declare readonly hasStripeTarget: boolean;
  declare readonly stripeTarget: HTMLElement;
  declare readonly hasAxisTarget: boolean;
  declare readonly axisTarget: HTMLElement;
  declare readonly hasBandCountTarget: boolean;
  declare readonly bandCountTarget: HTMLElement;
  declare readonly hasOkBannerTarget: boolean;
  declare readonly okBannerTarget: HTMLElement;
  declare readonly okMessageTarget: HTMLElement;
  declare readonly errorBannerTarget: HTMLElement;
  declare readonly errorMessageTarget: HTMLElement;
  declare readonly hasBandsSummaryTarget: boolean;
  declare readonly bandsSummaryTarget: HTMLElement;

  declare readonly maxValue: number;
  // Writable: the direction select is mirrored into this value at runtime so
  // the canvas re-colors immediately when the user flips Higher = worse/better
  // (the form auto-save returns head :ok with no HTML, so we can't rely on a
  // server re-render).
  declare higherIsWorseValue: boolean;
  declare readonly bandCountsValue: Record<string, number>;
  declare readonly okTemplateValue: string;
  declare readonly notIncreasingTemplateValue: string;
  declare readonly summaryTemplateValue: string;
  declare readonly summaryTemplateZeroValue: string;
  declare readonly bandCountTemplateValue: string;

  private rangesContainer: HTMLElement | null = null;
  private mutationObserver: MutationObserver | null = null;
  private boundOnInput = (event: Event) => this.onInput(event);

  connect() {
    this.rangesContainer = this.element.querySelector<HTMLElement>(
      "[data-scoring-config-target='rangesContainer']",
    );

    this.element.addEventListener("input", this.boundOnInput);
    this.element.addEventListener("change", this.boundOnInput);

    if (this.rangesContainer) {
      this.mutationObserver = new MutationObserver(() => this.render());
      this.mutationObserver.observe(this.rangesContainer, { childList: true });
    }

    // Defer so scoring-config#loadExistingConfig has populated the rows.
    setTimeout(() => this.render(), 0);
    requestAnimationFrame(() => this.render());
  }

  disconnect() {
    this.element.removeEventListener("input", this.boundOnInput);
    this.element.removeEventListener("change", this.boundOnInput);
    this.mutationObserver?.disconnect();
  }

  // Public action — wired to the Score direction <select>'s change event so
  // the canvas re-colors live as the user toggles Higher = worse/better.
  syncDirection(event: Event) {
    const target = event.target as HTMLSelectElement | null;
    if (!target) return;
    this.higherIsWorseValue = target.value !== "higher_is_better";
  }

  // Stimulus auto-invokes this whenever higherIsWorseValue changes (including
  // the initial assignment on connect; the targets aren't wired yet at that
  // point, so guard and let the connect-time render handle the first paint).
  higherIsWorseValueChanged() {
    if (!this.hasStripeTarget) return;
    this.render();
  }

  private onInput(event: Event) {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const field = target.getAttribute("data-field");
    if (field === "starts_at" || field === "label") {
      this.render();
    }
  }

  // Public so tests can drive it deterministically.
  render() {
    const bands = this.parseBands();
    this.renderStripe(bands);
    this.renderRowAffordances(bands);
    this.renderValidation(bands);
    this.renderSummary(bands.length);
  }

  private parseBands(): Band[] {
    if (!this.rangesContainer) return [];
    const rows = Array.from(
      this.rangesContainer.querySelectorAll<HTMLElement>("[data-range-row]"),
    );

    const thresholds = rows.map((row, index) => {
      if (index === 0) return 0;
      const raw = row.querySelector<HTMLInputElement>("[data-field='starts_at']")?.value ?? "";
      const parsed = parseInt(raw, 10);
      return Number.isNaN(parsed) ? null : parsed;
    });

    return rows.map((row, index) => {
      const startsAt = thresholds[index];
      const min = startsAt ?? 0;
      const nextStartsAt = thresholds[index + 1];
      const max =
        index < rows.length - 1
          ? (nextStartsAt ?? min) - 1
          : this.maxValue;
      const palette = bandColor(index, rows.length, this.higherIsWorseValue);
      return {
        index,
        startsAt,
        min,
        max,
        label: row.querySelector<HTMLInputElement>("[data-field='label']")?.value.trim() ?? "",
        bg: palette.bg,
        text: palette.text,
        row,
      };
    });
  }

  private renderStripe(bands: Band[]) {
    if (this.hasBandCountTarget) {
      this.bandCountTarget.textContent = this.format(this.bandCountTemplateValue, {
        count: bands.length,
      });
    }

    if (this.hasStripeTarget) {
      this.stripeTarget.replaceChildren();
      for (const band of bands) {
        const span = Math.max(1, band.max - band.min + 1);
        const segment = document.createElement("div");
        segment.className =
          "flex flex-col items-center justify-center gap-px px-2 overflow-hidden whitespace-nowrap text-center border-r border-black/10 last:border-r-0";
        segment.style.flex = String(span);
        segment.style.background = band.bg;
        segment.style.color = band.text;

        const label = document.createElement("span");
        label.className = "text-[11px] font-medium leading-tight truncate w-full";
        label.textContent = band.label || `${band.min}–${band.max}`;

        const range = document.createElement("span");
        range.className = "font-mono text-[10px] opacity-70";
        range.textContent = `${band.min}–${band.max}`;

        segment.append(label, range);
        this.stripeTarget.appendChild(segment);
      }
    }

    if (this.hasAxisTarget) {
      this.axisTarget.replaceChildren();
      if (this.maxValue > 0) {
        const ticks = new Set<number>([0, this.maxValue]);
        for (let v = 5; v < this.maxValue; v += 5) ticks.add(v);
        for (const value of Array.from(ticks).sort((a, b) => a - b)) {
          const tick = document.createElement("span");
          tick.className = "absolute -translate-x-1/2 font-mono text-[10px] text-slate-400";
          tick.style.left = `${(value / this.maxValue) * 100}%`;
          tick.textContent = String(value);
          this.axisTarget.appendChild(tick);
        }
      }
    }
  }

  private renderRowAffordances(bands: Band[]) {
    for (const band of bands) {
      // 1-based band number — matches the index used in validation messages.
      const numberCell = band.row.querySelector<HTMLElement>("[data-band-number]");
      if (numberCell) numberCell.textContent = String(band.index + 1);

      const swatch = band.row.querySelector<HTMLElement>("[data-band-swatch]");
      if (swatch) {
        swatch.classList.remove("bg-slate-200");
        swatch.style.background = band.bg;
      }
      const rangeCell = band.row.querySelector<HTMLElement>("[data-band-range]");
      if (rangeCell) rangeCell.textContent = `${band.min}–${band.max}`;
    }
  }

  private renderValidation(bands: Band[]) {
    if (!this.hasOkBannerTarget) return;

    // Thresholds must strictly increase. The first band is locked to 0.
    for (let i = 1; i < bands.length; i++) {
      const current = bands[i];
      const previous = bands[i - 1];
      if (current.startsAt === null) continue;
      if (current.startsAt <= previous.min) {
        this.showBanner("error", this.format(this.notIncreasingTemplateValue, {
          index: i + 1,
          value: current.startsAt,
        }));
        return;
      }
    }

    if (bands.length > 0) {
      this.showBanner("ok", this.format(this.okTemplateValue, {
        count: bands.length,
        max: this.maxValue,
      }));
    } else {
      this.okBannerTarget.classList.add("hidden");
      this.errorBannerTarget.classList.add("hidden");
    }
  }

  private renderSummary(bandCount: number) {
    if (!this.hasBandsSummaryTarget) return;

    if (bandCount === 0) {
      this.bandsSummaryTarget.textContent = this.summaryTemplateZeroValue;
      return;
    }

    const responses = Object.values(this.bandCountsValue).reduce(
      (sum, n) => sum + (typeof n === "number" ? n : 0),
      0,
    );
    this.bandsSummaryTarget.textContent = this.format(this.summaryTemplateValue, {
      count: bandCount,
      max: this.maxValue,
      responses,
    });
  }

  private showBanner(level: "ok" | "error", message: string) {
    const ok = level === "ok";
    this.okBannerTarget.classList.toggle("hidden", !ok);
    this.errorBannerTarget.classList.toggle("hidden", ok);
    if (ok) {
      this.okMessageTarget.textContent = message;
    } else {
      this.errorMessageTarget.textContent = message;
    }
  }

  private format(template: string, vars: Record<string, string | number>): string {
    return template.replace(/%\{(\w+)\}/g, (_, key) => String(vars[key] ?? ""));
  }
}
